import { Typography } from "@mui/material";
import { CSSObject } from "types/types";
import ToolBar from "./ToolBar";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import StudentUsage, { IStudentUsageData } from "./StudentUsage";
import {
  useFetchStudentsUsageQuery,
  useFetchTeachersUsageQuery,
} from "app/services/reports";
import {
  StudentUsageSkeleton,
  TeacherStatisticsSkeleton,
} from "./UsageSkeletons";
const customStyles = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 4,
    padding: "20px 0 30px",
    "@media print": {
      breakInside: "auto",
      colorAdjust: "exact",
    },
  },
  statistics: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& > span": {
      padding: "10px 30px",
      fontWeight: "bold",
      fontSize: "16px",
    },
  },
} satisfies CSSObject;
interface ITeachersUsageData {
  totalTeachers: number;
  teachersWithAssignment: number;
  activeTeachers: number;
}

function getCurrentWeekRange(): { startOfWeek: Date; endOfWeek: Date } {
  const today = new Date();
  const dayOfWeek = today.getDay();

  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() + diffToMonday); // Set to Monday

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return { startOfWeek, endOfWeek };
}
const toTwoDigits = (a: number) => (a < 10 ? "0" + a : a.toString());
const toDate = (date: Date) => {
  if (!date) return;
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${date.getFullYear()}-${toTwoDigits(month)}-${toTwoDigits(day)}`;
};

const TeacherStudentUsage = () => {
  const { t } = useTranslation();
  const [teachersUsage, setTeachersUsage] = useState<ITeachersUsageData>();
  const [studentsUsage, setStudentsUsage] = useState<IStudentUsageData[]>();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    const { startOfWeek, endOfWeek } = getCurrentWeekRange();
    setDateRange([startOfWeek, endOfWeek]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      setTeachersUsage(undefined);
      setStudentsUsage(undefined);
    }
  }, [startDate, endDate]);

  const { data: teacherUsageData, isLoading: isTeacherUsageDataLoading } =
    useFetchTeachersUsageQuery(
      { startDate: toDate(startDate!)!, endDate: toDate(endDate!)! },
      {
        skip: !startDate || !endDate,
      }
    );
  const { data: studentUsageData, isLoading: isStudentUsageDataLoading } =
    useFetchStudentsUsageQuery(
      { startDate: toDate(startDate!)!, endDate: toDate(endDate!)! },
      {
        skip: !startDate || !endDate,
      }
    );

  useEffect(() => {
    if (teacherUsageData?._statusCode === 200 && teacherUsageData?._entity) {
      const {
        _activeTeachers: activeTeachers,
        _teachersWithAssignment: teachersWithAssignment,
        _totalTeachers: totalTeachers,
      } = teacherUsageData._entity;
      setTeachersUsage({
        totalTeachers,
        teachersWithAssignment,
        activeTeachers,
      });
    }

    if (studentUsageData?._statusCode === 200 && studentUsageData?._entity) {
      const grades = studentUsageData._entity._grades.map((grade) => ({
        gradeId: grade._gradeId,
        gradeCode: grade._gradeCode,
        totalStudents: grade._totalStudents,
        studentsWithSomeCompletion: grade._studentsWithSomeCompletion,
        activeStudents: grade._activeStudents,
      }));
      const totalCounts = grades.reduce(
        (total, grade) => ({
          ...total,
          totalStudents: total.totalStudents + grade.totalStudents,
          studentsWithSomeCompletion:
            total.studentsWithSomeCompletion + grade.studentsWithSomeCompletion,
          activeStudents: total.activeStudents + grade.activeStudents,
        }),
        {
          gradeId: 0,
          gradeCode: "Grand Total",
          totalStudents: 0,
          studentsWithSomeCompletion: 0,
          activeStudents: 0,
        }
      );
      grades.push(totalCounts);
      setStudentsUsage(grades);
    }
  }, [studentUsageData, teacherUsageData]);

  return (
    <Typography component="div" sx={customStyles.root}>
      <Typography
        component="span"
        sx={{ fontSize: "22px", color: "rgb(60, 180, 229)" }}
      >
        {t("Teachers & Students Status Report")}
      </Typography>
      <ToolBar setDateRange={setDateRange} dateRange={dateRange} />
      {isTeacherUsageDataLoading || !teachersUsage ? (
        <TeacherStatisticsSkeleton />
      ) : (
        <Typography component="div" sx={customStyles.statistics}>
          <Typography component="span">
            {t("Total teachers : {{total}}", {
              total: teachersUsage?.totalTeachers,
            })}
          </Typography>
          <Typography component="span">
            {t("Active teachers: {{active}}", {
              active: teachersUsage?.activeTeachers,
            })}
          </Typography>
          <Typography component="span">
            {t("Assigned teachers: {{assigned}}", {
              assigned: teachersUsage?.teachersWithAssignment,
            })}
          </Typography>
        </Typography>
      )}
      {isStudentUsageDataLoading || !studentsUsage ? (
        <StudentUsageSkeleton />
      ) : (
        <StudentUsage data={studentsUsage} />
      )}
    </Typography>
  );
};

export default TeacherStudentUsage;
